// @ts-check
/** hCaptcha needs a genuine native submit (fetch POST is rejected, preventDefault forces a reload),
 * so this form targets a hidden iframe: native submit runs, but the top page never reloads. */

import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLDivElement} [inputRow]
 * @property {HTMLInputElement} [email]
 */

/**
 * @extends {Component<Refs>}
 */
class NewsletterForm extends Component {
  /** @type {HTMLIFrameElement | undefined} */
  #iframe;

  /** True between a submit and the iframe result load, so the initial blank load is ignored. */
  #armed = false;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    // Fresh controller per connect, so a reconnect (e.g. a Section Rendering API morph)
    // rebinds the load listener instead of attaching it to an already-aborted signal.
    this.#controller = new AbortController();

    const iframe = document.createElement('iframe');
    iframe.hidden = true;
    iframe.title = '';
    iframe.setAttribute('tabindex', '-1');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.name = `${form.id || 'newsletter'}-sink`;
    this.appendChild(iframe);
    this.#iframe = iframe;

    // Shopify's captcha submits the real form and honours this target, so the response lands in
    // the hidden iframe instead of reloading the top page.
    form.target = iframe.name;

    iframe.addEventListener('load', this.#handleResult, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#iframe?.remove();
  }

  /** Delegated from `on:submit="/handleSubmit"`. Deliberately does NOT preventDefault: the native
   * submit must proceed so Shopify's hCaptcha can run and inject its token. */
  handleSubmit = () => {
    this.#armed = true;
    this.#clearError();
  };

  #handleResult = () => {
    if (!this.#armed) return;
    this.#armed = false;

    const { succeeded, errorText } = this.#readResult();
    if (succeeded) this.#showSuccess();
    else this.#showError(errorText || this.dataset.error || '');

    // Release the full page the iframe just loaded.
    if (this.#iframe) this.#iframe.src = 'about:blank';
  };

  /** @returns {{ succeeded: boolean, errorText: string }} */
  #readResult() {
    try {
      const doc = this.#iframe?.contentDocument;
      const href = this.#iframe?.contentWindow?.location?.href ?? '';
      const succeeded =
        href.includes('customer_posted=true') ||
        Boolean(doc?.querySelector('.custom-footer__subscribe-success'));
      const errorText = succeeded
        ? ''
        : (doc?.querySelector('.custom-footer__subscribe-error')?.textContent?.trim() ?? '');
      return { succeeded, errorText };
    } catch (error) {
      // A SecurityError means the POST went through but the response lives on another host, so
      // treat it as success. Anything else is unexpected: log it and surface an error instead.
      if (error instanceof DOMException && error.name === 'SecurityError') {
        return { succeeded: true, errorText: '' };
      }
      console.warn('[newsletter-form] could not read iframe result', error);
      return { succeeded: false, errorText: '' };
    }
  }

  #showSuccess() {
    this.#clearError();
    const message = document.createElement('p');
    message.className = 'custom-footer__subscribe-success';
    message.setAttribute('role', 'status');
    message.setAttribute('tabindex', '-1');
    message.textContent = this.dataset.success || '';
    // Replace the whole form, not just the input row: Shopify leaves the invisible h-captcha div
    // and form_key input as flex siblings, whose gap would top-align the message in a tall form.
    (this.querySelector('form') ?? this.refs.inputRow)?.replaceWith(message);
    message.focus();
  }

  /** @param {string} text */
  #showError(text) {
    const email = this.refs.email;
    if (!email) return;
    const id = `${email.id}-error`;
    let error = this.querySelector(`#${CSS.escape(id)}`);
    if (!(error instanceof HTMLElement)) {
      error = document.createElement('p');
      error.id = id;
      error.className = 'custom-footer__subscribe-error';
      error.setAttribute('role', 'alert');
      this.refs.inputRow?.after(error);
    }
    error.textContent = text;
    email.setAttribute('aria-invalid', 'true');
    email.setAttribute('aria-describedby', id);
    email.focus();
  }

  #clearError() {
    const email = this.refs.email;
    if (!email) return;
    this.querySelector(`#${CSS.escape(`${email.id}-error`)}`)?.remove();
    email.removeAttribute('aria-invalid');
    email.removeAttribute('aria-describedby');
  }
}

if (!customElements.get('newsletter-form')) {
  customElements.define('newsletter-form', NewsletterForm);
}
